// src/pages/ImageSearchResults.ts
import "./ImageSearchResults.css";
import { renderSearchBar } from "../components/SearchBar";
import { renderSearchFooter } from "../components/SearchFooter";

const IMAGE_SEARCH_API_URL =
  "https://api.ayesearch.com/api/search/images/";

interface SearchImageResult {
  id: number;
  search_document_id: number;
  crawl_document_id: number | null;

  source_page_url: string;
  source_image_url: string;
  image_url: string;
  storage_key: string;

  title: string;
  alt_text: string;

  width: number | null;
  height: number | null;

  focal_x: number | null;
  focal_y: number | null;

  source_domain: string;
  content_hash: string;
  indexed_at: string;
}

interface SearchImageResponse {
  query: string;
  count: number;
  results: SearchImageResult[];
}


const VITE_PUBLIC_BASE =
  import.meta.env.BASE_URL;

const OWNED_SITE_IMAGE_ALIASES: Record<
  string,
  "ayesearch" | "orechat" | "amailme" | "empty"
> = {
  "ayesearch": "ayesearch",
  "aye search": "ayesearch",
  "ayesearch.com": "ayesearch",

  "orechat": "orechat",
  "ore chat": "orechat",
  "orechat.ayesearch.com": "orechat",

  "amailme": "amailme",
  "amail": "amailme",
  "a mail": "amailme",
  "amailme.ayesearch.com": "amailme",

  "adrive": "empty",
  "a drive": "empty",
  "adrive.ayesearch.com": "empty",

  "aikipedia": "empty",
  "ayekipedia": "empty",
  "aye kipedia": "empty",
  "aikipedia.ayesearch.com": "empty",

  "anotes": "empty",
  "anote": "empty",
  "anotes.ayesearch.com": "empty",

  "updates": "empty",
  "ayesearch updates": "empty",
  "updates.ayesearch.com": "empty",

  "ayesearch ads": "empty",
  "aye search ads": "empty",
  "ads": "empty",
  "ads.ayesearch.com": "empty",

  "aappstore": "empty",
  "a app store": "empty",
  "ayesearch app store": "empty",
  "aappstore.ayesearch.com": "empty",

  "agamestore": "empty",
  "a game store": "empty",
  "ayesearch games": "empty",
  "agamestore.ayesearch.com": "empty",

  "adocs": "empty",
  "a docs": "empty",
  "adocs.ayesearch.com": "empty",

  "ayelinks": "empty",
  "aye links": "empty",
  "ayelinks.com": "empty",
};

function normalizeOwnedImageQuery(
  query: string,
): string {
  return query
    .toLowerCase()
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/+$/, "")
    .replace(/\s+/g, " ");
}

function makeCuratedImage(
  id: number,
  title: string,
  sourcePageUrl: string,
  imageUrl: string,
  sourceDomain: string,
  width: number | null = null,
  height: number | null = null,
): SearchImageResult {
  return {
    id,
    search_document_id: 0,
    crawl_document_id: null,
    source_page_url: sourcePageUrl,
    source_image_url: imageUrl,
    image_url: imageUrl,
    storage_key: "",
    title,
    alt_text: title,
    width,
    height,
    focal_x: null,
    focal_y: null,
    source_domain: sourceDomain,
    content_hash: "",
    indexed_at: "",
  };
}

function getCuratedOwnedSiteImages(
  query: string,
): SearchImageResult[] | null {
  const normalized =
    normalizeOwnedImageQuery(query);

  const type =
    OWNED_SITE_IMAGE_ALIASES[normalized];

  if (!type) {
    return null;
  }

  if (type === "empty") {
    return [];
  }

  if (type === "ayesearch") {
    return [
      makeCuratedImage(
        -101,
        "AyeSearch Browser",
        "https://ayesearch.com/downloads/",
        `${VITE_PUBLIC_BASE}us/aysearch-browser.png`,
        "ayesearch.com",
      ),
      makeCuratedImage(
        -102,
        "AyeSearch Incognito",
        "https://ayesearch.com/downloads/",
        `${VITE_PUBLIC_BASE}us/aysearch-incognito.png`,
        "ayesearch.com",
      ),
      makeCuratedImage(
        -103,
        "AyeSearch",
        "https://ayesearch.com",
        "/static/ayesearch/Ayesearch_Icon.png",
        "ayesearch.com",
      ),
    ];
  }

  if (type === "orechat") {
    return [
      makeCuratedImage(
        -201,
        "OreChat",
        "https://orechat.ayesearch.com",
        "/static/ayesearch/OreChat_Icon.png",
        "orechat.ayesearch.com",
      ),
    ];
  }

  return [
    makeCuratedImage(
      -301,
      "AMailMe",
      "https://amailme.ayesearch.com",
      "/static/ayesearch/Amailme_Icon.png",
      "amailme.ayesearch.com",
    ),
  ];
}

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function displayDomain(
  result: SearchImageResult,
): string {
  if (result.source_domain) {
    return result.source_domain.replace(/^www\./, "");
  }

  try {
    return new URL(
      result.source_page_url,
    ).hostname.replace(/^www\./, "");
  } catch {
    return "Web";
  }
}

function renderImageCard(
  result: SearchImageResult,
): string {
  const width =
    result.width && result.width > 0
      ? result.width
      : undefined;

  const height =
    result.height && result.height > 0
      ? result.height
      : undefined;

  const sizeAttributes =
    width && height
      ? `width="${width}" height="${height}"`
      : "";

  return `
    <article class="image-search-card">
      <a
        class="image-search-image-link"
        href="${escapeHtml(result.source_page_url)}"
        target="_blank"
        rel="noreferrer"
        aria-label="${escapeHtml(result.title || "Open source page")}"
      >
        <img
          class="image-search-image"
          src="${escapeHtml(result.image_url)}"
          alt="${escapeHtml(
            result.alt_text ||
            result.title ||
            "Search image",
          )}"
          ${sizeAttributes}
          loading="lazy"
          decoding="async"
          referrerpolicy="no-referrer"
        />

        <div class="image-search-hover">
          <span>View source</span>
        </div>
      </a>

      <div class="image-search-meta">
        <a
          class="image-search-title"
          href="${escapeHtml(result.source_page_url)}"
          target="_blank"
          rel="noreferrer"
        >
          ${escapeHtml(result.title || "Untitled image")}
        </a>

        <div class="image-search-source">
          <span class="image-search-source-dot"></span>
          <span>${escapeHtml(displayDomain(result))}</span>
        </div>
      </div>
    </article>
  `;
}

function renderLoading(
  container: HTMLElement,
): void {
  container.innerHTML = `
    <div class="image-search-loading-grid">
      ${Array.from(
        { length: 10 },
        (_, index) => `
          <div
            class="image-search-skeleton"
            style="height:${150 + (index % 4) * 46}px"
          ></div>
        `,
      ).join("")}
    </div>
  `;
}

function renderEmpty(
  container: HTMLElement,
  query: string,
): void {
  container.innerHTML = `
    <div class="image-search-empty">
      <h2>No images found</h2>
      <p>
        AyeSearch does not have indexed images for
        <strong>${escapeHtml(query || "this search")}</strong>
        yet.
      </p>
    </div>
  `;
}

async function loadImages(
  query: string,
  resultsContainer: HTMLElement,
  countContainer: HTMLElement,
): Promise<void> {
  if (!query.trim()) {
    countContainer.textContent = "";
    renderEmpty(resultsContainer, "");
    return;
  }

  const curatedImages =
    getCuratedOwnedSiteImages(query);

  if (curatedImages !== null) {
    countContainer.textContent =
      curatedImages.length === 1
        ? "1 image"
        : `${curatedImages.length} images`;

    if (!curatedImages.length) {
      renderEmpty(resultsContainer, query);
      return;
    }

    resultsContainer.innerHTML = `
      <div class="image-search-masonry">
        ${curatedImages
          .map(renderImageCard)
          .join("")}
      </div>
    `;
    return;
  }

  renderLoading(resultsContainer);

  try {
    const url = new URL(IMAGE_SEARCH_API_URL);
    url.searchParams.set("q", query);
    url.searchParams.set("limit", "100");

    const response = await fetch(
      url.toString(),
      {
        headers: {
          Accept: "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Image search returned HTTP ${response.status}`,
      );
    }

    const payload =
      (await response.json()) as SearchImageResponse;

    countContainer.textContent =
      payload.count === 1
        ? "1 indexed image"
        : `${payload.count.toLocaleString()} indexed images`;

    if (!payload.results.length) {
      renderEmpty(resultsContainer, query);
      return;
    }

    resultsContainer.innerHTML = `
      <div class="image-search-masonry">
        ${payload.results.map(renderImageCard).join("")}
      </div>
    `;
  } catch (error) {
    console.error(
      "Image search request failed:",
      error,
    );

    countContainer.textContent = "";

    resultsContainer.innerHTML = `
      <div class="image-search-error">
        <h2>Images are temporarily unavailable</h2>
        <p>
          AyeSearch could not load the image index.
        </p>
      </div>
    `;
  }
}

export function renderImageSearchResults(
  query: string,
): void {
  const app = document.getElementById("app");
  if (!app) return;

  document.title = query.trim()
    ? `${query} Images - AyeSearch`
    : "Images - AyeSearch";

  app.innerHTML = `
    <div class="image-search-page">
      <div
        id="image-searchbar-container"
        class="searchbar-container"
      ></div>

      <main class="image-search-main">
        <div class="image-search-heading-row">
          <div>
            <span class="image-search-eyebrow">
              IMAGES
            </span>

            <h1>
              ${query.trim()
                ? escapeHtml(query)
                : "Image search"}
            </h1>
          </div>

          <span
            id="image-search-count"
            class="image-search-count"
          ></span>
        </div>

        <div
          id="image-search-results"
          class="image-search-results"
        ></div>
      </main>

      <div id="image-search-footer"></div>
    </div>
  `;

  const searchBarContainer =
    document.getElementById(
      "image-searchbar-container",
    );

  if (searchBarContainer) {
    renderSearchBar(
      searchBarContainer,
      query,
    );
  }

  const footerContainer =
    document.getElementById(
      "image-search-footer",
    );

  if (footerContainer) {
    renderSearchFooter(footerContainer);
  }

  const resultsContainer =
    document.getElementById(
      "image-search-results",
    );

  const countContainer =
    document.getElementById(
      "image-search-count",
    );

  if (
    !resultsContainer ||
    !countContainer
  ) {
    return;
  }

  void loadImages(
    query,
    resultsContainer,
    countContainer,
  );
}
